import { useState, useEffect } from 'react';
import api from '../../lib/api';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Switch } from '../../components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { CalendarOff, Plus, Trash2, Clock } from 'lucide-react';
import { toast } from 'sonner';

export default function AdminBlockedSlots() {
  const [slots, setSlots] = useState([]);
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState({ date: '', start_time: '09:00', end_time: '18:00', staff_id: '', reason: '', recurring: false, day_of_week: -1 });

  const fetchSlots = async () => {
    try {
      const [s, st] = await Promise.all([api.get('/blocked-slots'), api.get('/bookings/staff-list')]);
      setSlots(s.data);
      setStaff(st.data);
    } catch (e) { console.error(e); }
    setLoading(false);
  };
  useEffect(() => { fetchSlots(); }, []);

  const saveSlot = async () => {
    try {
      await api.post('/blocked-slots', form);
      toast.success('Blocked slot created');
      setDialogOpen(false);
      setForm({ date: '', start_time: '09:00', end_time: '18:00', staff_id: '', reason: '', recurring: false, day_of_week: -1 });
      fetchSlots();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const deleteSlot = async (id) => {
    if (!window.confirm('Remove this blocked slot?')) return;
    await api.delete(`/blocked-slots/${id}`);
    toast.success('Blocked slot removed');
    fetchSlots();
  };

  const dayNames = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

  if (loading) return <div className="flex justify-center py-20"><div className="animate-spin rounded-full h-8 w-8 border-2 border-[#2D2D2D] border-t-transparent" /></div>;

  return (
    <div className="space-y-6" data-testid="admin-blocked-slots-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-[#1A1A1A]" style={{ fontFamily: 'Manrope' }}>Blocked Slots</h1>
          <p className="text-sm text-[#5A5A5A] mt-1">Block timeslots to prevent bookings</p>
        </div>
        <Button className="rounded-full bg-[#2D2D2D] text-white" onClick={() => setDialogOpen(true)} data-testid="add-blocked-slot-btn">
          <Plus className="w-4 h-4 mr-2" /> Block Slot
        </Button>
      </div>

      {slots.length === 0 ? (
        <Card className="rounded-2xl border-[#E5E5E0] border-dashed bg-white">
          <CardContent className="p-8 text-center">
            <CalendarOff className="w-10 h-10 text-[#D6D6D0] mx-auto mb-3" />
            <p className="text-sm text-[#5A5A5A]">No blocked slots. Clients can book any available time.</p>
          </CardContent>
        </Card>
      ) : (
        <Card className="rounded-2xl border-[#E5E5E0] bg-white overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow className="bg-[#F9F9F7]">
                <TableHead className="text-xs font-semibold text-[#5A5A5A]">Date</TableHead>
                <TableHead className="text-xs font-semibold text-[#5A5A5A]">Time</TableHead>
                <TableHead className="text-xs font-semibold text-[#5A5A5A]">Staff</TableHead>
                <TableHead className="text-xs font-semibold text-[#5A5A5A]">Reason</TableHead>
                <TableHead className="text-xs font-semibold text-[#5A5A5A]">Type</TableHead>
                <TableHead className="text-xs font-semibold text-[#5A5A5A]">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {slots.map(s => (
                <TableRow key={s.block_id} data-testid={`blocked-slot-row-${s.block_id}`}>
                  <TableCell className="text-sm font-medium">{s.recurring ? dayNames[s.day_of_week] || 'Every week' : s.date}</TableCell>
                  <TableCell className="text-sm flex items-center gap-1"><Clock className="w-3 h-3 text-[#888]" />{s.start_time} - {s.end_time}</TableCell>
                  <TableCell className="text-sm">{s.staff_name}</TableCell>
                  <TableCell className="text-sm text-[#5A5A5A]">{s.reason || '-'}</TableCell>
                  <TableCell><span className={`text-xs px-2 py-0.5 rounded ${s.recurring ? 'bg-purple-50 text-purple-700' : 'bg-amber-50 text-amber-700'}`}>{s.recurring ? 'Recurring' : 'One-time'}</span></TableCell>
                  <TableCell>
                    <button onClick={() => deleteSlot(s.block_id)} className="p-1.5 rounded-lg hover:bg-red-50" data-testid={`delete-blocked-${s.block_id}`}>
                      <Trash2 className="w-3.5 h-3.5 text-[#D9534F]" />
                    </button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="rounded-2xl max-w-md">
          <DialogHeader><DialogTitle style={{ fontFamily: 'Manrope' }}>Block Time Slot</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="flex items-center gap-3">
              <Switch checked={form.recurring} onCheckedChange={c => setForm({ ...form, recurring: c })} data-testid="recurring-switch" />
              <Label className="text-sm">Recurring (weekly)</Label>
            </div>
            {form.recurring ? (
              <div><Label className="text-sm">Day of Week</Label>
                <Select value={String(form.day_of_week)} onValueChange={v => setForm({ ...form, day_of_week: parseInt(v) })}>
                  <SelectTrigger className="mt-1 rounded-lg"><SelectValue placeholder="Select day" /></SelectTrigger>
                  <SelectContent>{dayNames.map((d, i) => <SelectItem key={i} value={String(i)}>{d}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            ) : (
              <div><Label className="text-sm">Date *</Label><Input type="date" value={form.date} onChange={e => setForm({ ...form, date: e.target.value })} className="mt-1 rounded-lg" data-testid="blocked-date-input" /></div>
            )}
            <div className="grid grid-cols-2 gap-4">
              <div><Label className="text-sm">Start Time *</Label><Input type="time" value={form.start_time} onChange={e => setForm({ ...form, start_time: e.target.value })} className="mt-1 rounded-lg" data-testid="blocked-start-input" /></div>
              <div><Label className="text-sm">End Time *</Label><Input type="time" value={form.end_time} onChange={e => setForm({ ...form, end_time: e.target.value })} className="mt-1 rounded-lg" data-testid="blocked-end-input" /></div>
            </div>
            <div><Label className="text-sm">Staff (optional)</Label>
              <Select value={form.staff_id || 'all'} onValueChange={v => setForm({ ...form, staff_id: v === 'all' ? '' : v })}>
                <SelectTrigger className="mt-1 rounded-lg"><SelectValue placeholder="All staff" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Staff</SelectItem>
                  {staff.map(s => <SelectItem key={s.user_id} value={s.user_id}>{s.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div><Label className="text-sm">Reason</Label><Input value={form.reason} onChange={e => setForm({ ...form, reason: e.target.value })} className="mt-1 rounded-lg" placeholder="e.g. Holiday, Maintenance" data-testid="blocked-reason-input" /></div>
          </div>
          <DialogFooter><Button className="rounded-full bg-[#2D2D2D] text-white" onClick={saveSlot} data-testid="save-blocked-slot-btn">Block Slot</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
